<template>
  <div>
    <h1 class="break-all">{{ question.question }}</h1>

    <p v-if="!question.isDraft()" style="white-space: pre-line">
      <b>{{ $t('components.questionStatistics.theseAreTheResults') }}</b>
    </p>

    <div
      v-if="
        (show_results_while_active && question.isActive()) ||
        question.isFinished()
      "
    >
      <div v-if="Object.keys(question.statistics).length > 0">
        <p class="text-uppercase" style="white-space: pre-line">
          {{ $t('components.questionStatistics.totalActiveVoters') }}
          <b>{{ total_active_voters }}</b>
        </p>
        <p class="text-uppercase" v-if="quorum === true">
          {{ $t('components.questionStatistics.quorumRequired') }}
          {{ quorumValue }}
          {{ $t('components.questionStatistics.voters') }} (<span
            v-if="quorumPassed"
            class="success--text"
            >dosegnut</span
          >
          <span v-else class="error--text">nije dosegnut</span>)
        </p>
        <p class="text-uppercase" style="white-space: pre-line">
          {{ $t('components.questionStatistics.totalVotes') }}
          <b>{{ getSum(question.statistics) }}</b>
        </p>
      </div>

      <p v-else>
        <b>{{ $t('components.questionStatistics.noResults') }}</b>
      </p>

      <div v-for="(answer, index, key) in question.answers" :key="key">
        <p style="display: flex; justify-content: space-between">
          <span style="word-wrap: anywhere">{{ answer.label }}</span>
          <span style="text-align: right">
            {{
              question.statistics[answer.id] !== undefined
                ? question.statistics[answer.id].votes
                : 0
            }}
            <strong
              >({{
                question.statistics[answer.id] !== undefined
                  ? question.statistics[answer.id].percentage
                  : 0
              }}%)</strong
            >
          </span>
        </p>

        <v-progress-linear
          :value="
            question.statistics[answer.id] !== undefined
              ? question.statistics[answer.id].percentage
              : 0
          "
          height="25"
        ></v-progress-linear>

        <v-chip
          class="mt-0 mb-4 mr-2 ml-0"
          outline
          label
          v-for="vote in answer.votes"
          :key="vote.id"
        >
          <div v-if="vote.voter !== null">
            <span v-if="vote.authorizer === null">
              <v-icon left small> fa-user-circle </v-icon>
              {{ vote.voter.name }} {{ vote.voter.surname }} ({{
                vote.voter.email
              }})
            </span>
            <span v-else-if="vote.authorizer !== undefined">
              <v-icon left small> fa-stamp </v-icon>
              {{ vote.authorizer.name }}
            </span>
          </div>
          <div v-if="vote.voter === null">
            <span>
              <v-icon left small> fa-user-circle </v-icon>
              {{ $t('labels.anonymous') }}
            </span>
          </div>
        </v-chip>

        <br />
      </div>
      <div v-if="question.abstention === true">
        <p style="display: flex; justify-content: space-between">
          <span>{{ $t('components.questionStatistics.abstentioned') }}</span>
          <span>
            {{
              question.statistics['abstention'] !== undefined
                ? question.statistics['abstention'].votes
                : 0
            }}
            <strong
              >({{
                question.statistics['abstention'] !== undefined
                  ? question.statistics['abstention'].percentage
                  : 0
              }}%)</strong
            >
          </span>
        </p>

        <v-progress-linear
          :value="
            question.statistics['abstention'] !== undefined
              ? question.statistics['abstention'].percentage
              : 0
          "
          height="25"
        ></v-progress-linear>

        <v-chip
          class="mt-0 mb-4 mr-2 ml-0"
          outline
          label
          v-for="vote in questionVotes.filter(
            vote =>
              vote.answer_id === null &&
              vote.authorizer !== undefined &&
              vote.voter !== null
          )"
          :key="vote.id"
        >
          <span v-if="vote.authorizer === null">
            <v-icon left small> fa-user-circle </v-icon>
            {{ vote.voter.name }} {{ vote.voter.surname }} ({{
              vote.voter.email
            }})
          </span>
          <span v-else-if="vote.authorizer !== undefined">
            <v-icon left small> fa-stamp </v-icon>
            {{ vote.authorizer.name }}
          </span>
        </v-chip>

        <br />
      </div>
    </div>

    <v-card
      class="mb-3 mt-3"
      v-if="
        (!show_results_while_active && question.isActive()) ||
        question.isFinished()
      "
    >
      <v-card-title>
        {{ $t('components.questionStatistics.voters') }}
        <v-spacer></v-spacer>
        <v-btn
          @click="downloadQuestionVoters"
          class="ml-0"
          color="primary"
          slot="activator"
          small
        >
          <v-icon small>fa-file-excel fa-fw</v-icon>&nbsp;{{
            $t('buttons.downloadVoters')
          }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="voterSearch"
          append-icon="fa-search"
          :label="$t('fields.search')"
          single-line
          hide-details
          clearable
        ></v-text-field>
      </v-card-title>
      <v-data-table
        :headers="voterHeaders"
        :items="allVoters"
        :search="voterSearch"
        :no-data-text="$t('alerts.noData')"
        :no-results-text="$t('alerts.noResults')"
        :rows-per-page-items="[20, 30, 40]"
        :rows-per-page-text="$t('dataTable.rowsPerPage')"
      >
        <template v-slot:items="props">
          <td>{{ props.item.name }}</td>
          <td>{{ props.item.surname }}</td>
          <td>{{ props.item.authorizer }}</td>
          <td>{{ props.item.email }}</td>
          <td>{{ props.item.mobile }}</td>
          <td>{{ props.item.weight }}</td>
          <td>
            {{ Array.isArray(props.item.tags) ? '' : props.item.tags }}
          </td>
          <td class="text-xs-right">
            {{ props.item.voted ? $t('buttons.yes') : $t('buttons.no') }}
          </td>
        </template>
      </v-data-table>
      <v-radio-group
        :label="$t('resultsLabels.voted') + ':'"
        v-model="votedFilter"
        row
        class="mt-0 pl-3"
      >
        <v-radio :label="$t('labels.yes')" :value="1" color="primary"></v-radio>
        <v-radio :label="$t('labels.no')" :value="2" color="primary"></v-radio>
        <v-radio :label="$t('labels.all')" :value="0" color="primary"></v-radio>
      </v-radio-group>
    </v-card>

    <v-btn
      color="primary"
      small
      class="ml-0"
      v-if="question.isActive()"
      @click="$emit('get-question', question)"
    >
      {{ $t('components.questionStatistics.refreshResults') }}
    </v-btn>

    <blockquote v-if="question.isDraft()">
      {{ $t('components.questionStatistics.notStarted') }}
    </blockquote>
  </div>
</template>

<script>
import Question from '@/models/Question.js'

export default {
  props: {
    question: Question,
    show_results_while_active: Boolean,
    total_active_voters: Number,
    quorum_value: String,
    quorum: Boolean,
    event: Object,
  },

  data() {
    return {
      voterSearch: '',
      voterHeaders: [
        { text: this.$t('fields.name'), value: 'name' },
        { text: this.$t('fields.surname'), value: 'surname' },
        { text: this.$t('fields.authorizer'), value: 'authorizer' },
        { text: this.$t('fields.email'), value: 'email' },
        { text: this.$t('fields.mobile'), value: 'mobile' },
        { text: this.$t('fields.weight'), value: 'weight' },
        { text: this.$t('fields.tags'), value: 'tags' },
        { text: this.$t('fields.voted'), value: 'voted' },
      ],
      votedFilter: 0,
    }
  },

  computed: {
    questionVotes() {
      return this.question.votes === undefined ? [] : this.question.votes
    },

    allVoters() {
      // first name, last name, authorizer, email, mobile, weight, tags, voted
      const voters = []

      for (const voter of this.event.voters) {
        voter.tags = JSON.stringify(voter.tags)
          .replaceAll('"', '')
          .replaceAll('[', '')
          .replaceAll(']', '')
        if (voter.authorizers.length > 0) {
          for (const authorizer of voter.authorizers) {
            let authorizerVoted =
              this.question.voters.filter(
                voter1 =>
                  voter1.authorizers.filter(
                    authorizer1 =>
                      authorizer1.id === authorizer.id &&
                      authorizer1.voted === true
                  ).length > 0
              ).length > 0
            if (this.votedFilter === 1) {
              if (authorizerVoted === true) {
                voters.push({
                  name: voter.name,
                  surname: voter.surname,
                  authorizer: authorizer.name,
                  email: voter.email,
                  mobile: voter.mobile,
                  weight: authorizer.weight,
                  tags: voter.tags,
                  voted: authorizerVoted,
                })
              }
            } else if (this.votedFilter === 2) {
              if (authorizerVoted === false) {
                voters.push({
                  name: voter.name,
                  surname: voter.surname,
                  authorizer: authorizer.name,
                  email: voter.email,
                  mobile: voter.mobile,
                  weight: authorizer.weight,
                  tags: voter.tags,
                  voted: authorizerVoted,
                })
              }
            } else {
              voters.push({
                name: voter.name,
                surname: voter.surname,
                authorizer: authorizer.name,
                email: voter.email,
                mobile: voter.mobile,
                weight: authorizer.weight,
                tags: voter.tags,
                voted: authorizerVoted,
              })
            }
          }
        } else {
          let voterVoted =
            this.question.voters.filter(
              voter1 => voter1.id === voter.id && voter1.voted === true
            ).length > 0
          if (this.votedFilter === 1) {
            if (voterVoted === true) {
              voters.push({
                name: voter.name,
                surname: voter.surname,
                authorizer: '',
                email: voter.email,
                mobile: voter.mobile,
                weight: voter.weight,
                tags: voter.tags,
                voted: voterVoted,
              })
            }
          } else if (this.votedFilter === 2) {
            if (voterVoted === false) {
              voters.push({
                name: voter.name,
                surname: voter.surname,
                authorizer: '',
                email: voter.email,
                mobile: voter.mobile,
                weight: voter.weight,
                tags: voter.tags,
                voted: voterVoted,
              })
            }
          } else {
            voters.push({
              name: voter.name,
              surname: voter.surname,
              authorizer: '',
              email: voter.email,
              mobile: voter.mobile,
              weight: voter.weight,
              tags: voter.tags,
              voted: voterVoted,
            })
          }
        }
      }

      return voters
    },

    quorumValue: function () {
      if (this.quorum === false) return 0

      if (
        this.question.quorum_value !== null &&
        this.question.quorum_value != ''
      ) {
        return this.question.quorum_value
      }

      return this.quorum_value
    },

    quorumPassed: function () {
      if (this.quorum === false) return false

      const number_of_votes = this.getSum(this.question.statistics)

      if (
        this.question.quorum_value !== null &&
        this.question.quorum_value != ''
      ) {
        if (number_of_votes >= this.question.quorum_value) {
          return true
        }

        return false
      } else if (number_of_votes >= this.quorum_value) {
        return true
      }

      return false
    },
  },

  methods: {
    getSum(statistics) {
      return Object.entries(statistics).reduce(function (
        accumulator,
        currentValue
      ) {
        return accumulator + currentValue[1].votes
      },
      0)
    },

    downloadQuestionVoters() {
      if (this.allVoters.length > 0) {
        this.$APICall(
          'post',
          'EXCEL',
          '/voters-question',
          this.allVoters,
          data => {
            window.open(data.link)
          },
          true,
          error => {
            handle_session_error(error, this.$store, this.$router, this.$prompt)
          }
        )
      } else {
        this.$alert(this.$t('alerts.excelNoVoters'))
      }
    },
  },
}
</script>
